import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  ReactNode,
} from "react";
import {
  CognitoIdentityProviderClient,
  GetUserCommand,
  InitiateAuthCommand,
  InitiateAuthCommandInput,
  RespondToAuthChallengeCommand,
  RespondToAuthChallengeCommandInput,
} from "@aws-sdk/client-cognito-identity-provider";

// TODO: Move this into feature/auth folder
interface AuthContextType {
  isAuthenticated: boolean;
  errorMessage: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  accessToken: string | null;
  userDetails: {
    userName?: string;
    email?: string;
    role?: string;
  } | null;
  setUserDetails: React.Dispatch<
    React.SetStateAction<{
      userName?: string;
      email?: string;
      role?: string;
    } | null>
  >;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [userDetails, setUserDetails] = useState<{
    userName?: string;
    email?: string;
    role?: string;
  } | null>(null);

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);

  const cognitoClient = new CognitoIdentityProviderClient({
    region: process.env.REACT_APP_REGION
      ? process.env.REACT_APP_REGION
      : "ap-south-1",
  });

  useEffect(() => {
    // Restore authentication state from localStorage
    const token = localStorage.getItem("idToken");
    const storedUserDetails = localStorage.getItem("userDetails");

    if (token) {
      setIsAuthenticated(true);
      setAccessToken(localStorage.getItem("accessToken") || null);

      if (storedUserDetails) {
        setUserDetails(JSON.parse(storedUserDetails));
      }
    }
  }, []);

  // Login function using Cognito authentication
  const login = async (email: string, password: string) => {
    const params: InitiateAuthCommandInput = {
      AuthFlow: "USER_PASSWORD_AUTH",
      ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID
        ? process.env.REACT_APP_COGNITO_CLIENT_ID
        : "r3a1kq4e4qp726c8fprl9fp0b",
      AuthParameters: {
        USERNAME: email,
        PASSWORD: password,
      },
    };

    try {
      const command = new InitiateAuthCommand(params);
      let obj = await cognitoClient.send(command);

      if (obj.ChallengeName && obj.ChallengeName === "NEW_PASSWORD_REQUIRED") {
        obj = await setNewPassword(email, obj.Session);
      }

      const AuthenticationResult = obj.AuthenticationResult;
      console.log(AuthenticationResult);

      if (AuthenticationResult) {
        localStorage.setItem("idToken", AuthenticationResult.IdToken || "");
        localStorage.setItem(
          "accessToken",
          AuthenticationResult.AccessToken || ""
        );
        localStorage.setItem(
          "refreshToken",
          AuthenticationResult.RefreshToken || ""
        );
        localStorage.setItem("refreshTokenTimestamp", Date.now().toString());

        const getUserCommand = new GetUserCommand({
          AccessToken: AuthenticationResult.AccessToken,
        });

        const response = await cognitoClient.send(getUserCommand);

        const userDetails = {
          role: response?.UserAttributes?.find(
            (attribute) => attribute.Name === "custom:system_role"
          )?.Value,
          // role: "BACK_OFFICE_USER",
          userName: response?.Username,
          email: response?.UserAttributes?.find(
            (attribute) => attribute.Name === "email"
          )?.Value,
        };

        setUserDetails(userDetails);
        setIsAuthenticated(true);
        setErrorMessage(null);
        setAccessToken(AuthenticationResult.AccessToken || null);
      }
    } catch (error) {
      console.error("Error logging in:", error);
      setErrorMessage("Invalid login credentials. Please try again.");
    }
  };

  const setNewPassword = async (
    userName: string,
    Session: string | undefined
  ) => {
    const params: RespondToAuthChallengeCommandInput = {
      ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID
        ? process.env.REACT_APP_COGNITO_CLIENT_ID
        : "r3a1kq4e4qp726c8fprl9fp0b",
      ChallengeName: "NEW_PASSWORD_REQUIRED",
      ChallengeResponses: {
        NEW_PASSWORD: "P@ssw0rd",
        USERNAME: userName,
      },
      Session: Session,
    };

    const respondToChallengeCommand = new RespondToAuthChallengeCommand(params);
    const challengeResponseObj = await cognitoClient.send(
      respondToChallengeCommand
    );
    return challengeResponseObj;
  };

  // Logout function to clear localStorage
  const logout = () => {
    localStorage.removeItem("idToken");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("refreshTokenTimestamp");
    localStorage.removeItem("userDetails");
    setIsAuthenticated(false);
    setAccessToken(null);
    setUserDetails(null);
  };

  // Refresh token function to get a new access token using the refresh token
  const refreshToken = async () => {
    const refreshToken = localStorage.getItem("refreshToken");

    if (!refreshToken) {
      setErrorMessage("No refresh token found. Please log in again.");
      return;
    }

    const params: InitiateAuthCommandInput = {
      AuthFlow: "REFRESH_TOKEN_AUTH",
      ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID
        ? process.env.REACT_APP_COGNITO_CLIENT_ID
        : "r3a1kq4e4qp726c8fprl9fp0b",
      AuthParameters: {
        REFRESH_TOKEN: refreshToken,
      },
    };

    try {
      const command = new InitiateAuthCommand(params);
      const { AuthenticationResult } = await cognitoClient.send(command);

      if (AuthenticationResult) {
        localStorage.setItem(
          "accessToken",
          AuthenticationResult.AccessToken || ""
        );
        setAccessToken(AuthenticationResult.AccessToken || null);
        setErrorMessage(null);
      }
    } catch (error) {
      console.error("Error refreshing token:", error);
      setErrorMessage("Session expired. Please log in again.");
      logout(); // Log the user out if refreshing the token fails
    }
  };

  useEffect(() => {
    localStorage.setItem("userDetails", JSON.stringify(userDetails));
  }, [userDetails]);

  return (
    <AuthContext.Provider
      value={{
        userDetails,
        accessToken,
        isAuthenticated,
        errorMessage,
        login,
        logout,
        refreshToken,
        setUserDetails,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the AuthContext
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
